import { Box, Stack, Typography } from '@mui/material';
import { Checkbox } from 'antd';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { updateTimeSettings } from 'store/reducers/inputReducer';
import {
  selectTimeSetting
} from 'store/reducers/selectors';

import { STATIC_TABLE_SETTINGS } from 'store/reducers/constants/constants.js';

const TimeSettingsCheckboxes = (props) => {
  const { tableName } = props;
  if (!tableName) {
    throw new Error('tableName is a required prop for TimeSettingsCheckboxes');
  }

  const timeUnit = useSelector(selectTimeSetting('timeUnit'));

  const usePickupEnd = useSelector(selectTimeSetting('usePickupEnd', tableName));
  const usePickupStart = useSelector(selectTimeSetting('usePickupStart', tableName));
  const usePickupExact = useSelector(selectTimeSetting('usePickupExact', tableName));
  const useDropoffEnd = useSelector(selectTimeSetting('useDropoffEnd', tableName));
  const useDropoffStart = useSelector(selectTimeSetting('useDropoffStart', tableName));
  const useDropoffExact = useSelector(selectTimeSetting('useDropoffExact', tableName));

  const tableTimeSettings = STATIC_TABLE_SETTINGS?.[tableName]?.timeWindows;

  const dispatch = useDispatch();

  const handleTimeWindowChange = (timeWindow, val) => {
    dispatch(updateTimeSettings({ settingNames: [timeWindow], tableName, settingValues: [!val] }));

    let columnName;
    let offColumnName;

    if (!tableTimeSettings) return;

    if (timeWindow === 'usePickupEnd') {
      if (timeUnit === 'clock') {
        columnName = tableTimeSettings.pickupEnd.columnNameTime;
        offColumnName = tableTimeSettings.pickupExact && [tableTimeSettings.pickupExact.columnNameTime];
      } else {
        columnName = tableTimeSettings.pickupEnd.columnNameInt;
        offColumnName = tableTimeSettings.pickupExact && [tableTimeSettings.pickupExact.columnNameInt];
      }

      if (!val) {
        if (!offColumnName) {
          dispatch(updateTimeSettings({ settingNames: ['usePickupExact'], tableName, settingValues: [false] }));
        }
        if (offColumnName) {
          dispatch(
            updateTimeSettings({
              tableName,
              columnValues: [false],
              columnNames: offColumnName,
              settingNames: ['usePickupExact'],
              settingValues: [false],
            })
          );
        }
      }
    }

    if (timeWindow === 'usePickupStart') {
      if (timeUnit === 'clock') {
        columnName = tableTimeSettings.pickupStart.columnNameTime;
        offColumnName = tableTimeSettings.pickupExact && [tableTimeSettings.pickupExact.columnNameTime];
      } else {
        columnName = tableTimeSettings.pickupStart.columnNameInt;
        offColumnName = tableTimeSettings.pickupExact && [tableTimeSettings.pickupExact.columnNameInt];
      }

      if (!val) {
        if (!offColumnName) {
          dispatch(updateTimeSettings({ settingNames: ['usePickupExact'], tableName, settingValues: [false] }));
        }
        if (offColumnName) {
          dispatch(
            updateTimeSettings({
              tableName,
              columnValues: [false],
              columnNames: offColumnName,
              settingNames: ['usePickupExact'],
              settingValues: [false],
            })
          );
        }
      }
    }

    if (timeWindow === 'usePickupExact') {
      if (timeUnit === 'clock') {
        columnName = tableTimeSettings.pickupExact.columnNameTime;
        offColumnName = [tableTimeSettings.pickupStart.columnNameTime, tableTimeSettings.pickupEnd.columnNameTime];
      } else {
        columnName = tableTimeSettings.pickupExact.columnNameInt;
        offColumnName = [tableTimeSettings.pickupStart.columnNameInt, tableTimeSettings.pickupEnd.columnNameInt];
      }

      if (!val) {
        dispatch(
          updateTimeSettings({
            tableName,
            columnValues: [false, false],
            columnNames: offColumnName,
            settingNames: ['usePickupStart', 'usePickupEnd'],
            settingValues: [false, false],
          })
        );
      }
    }

    if (timeWindow === 'useDropoffEnd') {
      if (timeUnit === 'clock') {
        columnName = tableTimeSettings.dropoffEnd.columnNameTime;
        offColumnName = tableTimeSettings.dropoffExact && [tableTimeSettings.dropoffExact.columnNameTime];
      } else {
        columnName = tableTimeSettings.dropoffEnd.columnNameInt;
        offColumnName = tableTimeSettings.dropoffExact && [tableTimeSettings.dropoffExact.columnNameInt];
      }

      if (!val) {
        if (!offColumnName) {
          dispatch(updateTimeSettings({ settingNames: ['useDropoffExact'], tableName, settingValues: [false] }));
        }
        if (offColumnName) {
          dispatch(
            updateTimeSettings({
              tableName,
              columnValues: [false],
              columnNames: offColumnName,
              settingNames: ['useDropoffExact'],
              settingValues: [false],
            })
          );
        }
      }
    }

    if (timeWindow === 'useDropoffStart') {
      if (timeUnit === 'clock') {
        columnName = tableTimeSettings.dropoffStart.columnNameTime;
        offColumnName = tableTimeSettings.dropoffExact && [tableTimeSettings.dropoffExact.columnNameTime];
      } else {
        columnName = tableTimeSettings.dropoffStart.columnNameInt;
        offColumnName = tableTimeSettings.dropoffExact && [tableTimeSettings.dropoffExact.columnNameInt];
      }

      if (!val) {
        if (!offColumnName) {
          dispatch(updateTimeSettings({ settingNames: ['useDropoffExact'], tableName, settingValues: [false] }));
        }
        if (offColumnName) {
          dispatch(
            updateTimeSettings({
              tableName,
              columnValues: [false],
              columnNames: offColumnName,
              settingNames: ['useDropoffExact'],
              settingValues: [false],
            })
          );
        }
      }
    }

    if (timeWindow === 'useDropoffExact') {
      if (timeUnit === 'clock') {
        columnName = tableTimeSettings.dropoffExact.columnNameTime;
        offColumnName = [tableTimeSettings.dropoffStart.columnNameTime, tableTimeSettings.dropoffEnd.columnNameTime];
      } else {
        columnName = tableTimeSettings.dropoffExact.columnNameInt;
        offColumnName = [tableTimeSettings.dropoffStart.columnNameInt, tableTimeSettings.dropoffEnd.columnNameInt];
      }

      if (!val) {
        dispatch(
          updateTimeSettings({
            tableName,
            columnValues: [false, false],
            columnNames: offColumnName,
            settingNames: ['useDropoffStart', 'useDropoffEnd'],
            settingValues: [false, false],
          })
        );
      }
    }

    dispatch(updateTimeSettings({ tableName, columnValues: [!val], columnNames: [columnName] }));
  };

  if (!tableTimeSettings) return null;

  const pickupOptions = [
    tableTimeSettings?.pickupEnd && {
      key: 'usePickupEnd',
      label: tableTimeSettings.pickupEnd.description,
      checked: usePickupEnd,
    },
    tableTimeSettings?.pickupStart && {
      key: 'usePickupStart',
      label: tableTimeSettings.pickupStart.description,
      checked: usePickupStart,
    },
    tableTimeSettings?.pickupExact && {
      key: 'usePickupExact',
      label: tableTimeSettings.pickupExact.description,
      checked: usePickupExact,
    },
  ].filter(Boolean);

  const dropoffOptions = [
    tableTimeSettings?.dropoffEnd && {
      key: 'useDropoffEnd',
      label: tableTimeSettings.dropoffEnd.description,
      checked: useDropoffEnd,
    },
    tableTimeSettings?.dropoffStart && {
      key: 'useDropoffStart',
      label: tableTimeSettings.dropoffStart.description,
      checked: useDropoffStart,
    },
    tableTimeSettings?.dropoffExact && {
      key: 'useDropoffExact',
      label: tableTimeSettings.dropoffExact.description,
      checked: useDropoffExact,
    },
  ].filter(Boolean);

  // Each label is a real <label htmlFor> tied to its checkbox: clicking toggles
  // natively (no onClick), and screen readers announce it as the checkbox's name.
  const renderOption = (opt) => {
    const checkboxId = `time-window-${tableName}-${opt.key}`;
    return (
      <div
        key={opt.key}
        style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '3px 0' }}
      >
        <Checkbox
          id={checkboxId}
          checked={Boolean(opt.checked)}
          onChange={() => handleTimeWindowChange(opt.key, opt.checked)}
          style={{ marginTop: '2px', flexShrink: 0 }}
        />
        <label
          htmlFor={checkboxId}
          style={{ fontSize: '14px', color: '#525f7f', lineHeight: '1.5', cursor: 'pointer', userSelect: 'none', marginBottom: 0 }}
        >
          {opt.label}
        </label>
      </div>
    );
  };

  return (
    <Stack spacing={1.25}>
      <Box>
        <Typography
          variant="caption"
          sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.8 }}
        >
          Pickup
        </Typography>
        <Box sx={{ mt: 0.5 }}>
          {pickupOptions.map(renderOption)}
        </Box>
      </Box>

      {dropoffOptions.length > 0 && (
        <Box sx={{ pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.8 }}
          >
            Dropoff
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            {dropoffOptions.map(renderOption)}
          </Box>
        </Box>
      )}
    </Stack>
  );
};

TimeSettingsCheckboxes.propTypes = {
  tableName: PropTypes.string.isRequired,
};

export default TimeSettingsCheckboxes;
